/**
 * Load and normalise the guide model.
 *
 * Exported models arrive with arbitrary root scales and axis conversions — the
 * reference model carries a 0.01 root scale plus a Blender Z-up→Y-up rotation.
 * Trusting the import would put the guide at whatever size the DCC tool decided,
 * so height is measured and normalised explicitly.
 */
import {
  AnimationClip,
  AnimationMixer,
  Box3,
  Group,
  Mesh,
  Vector3,
  type Material,
} from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

export interface MorphTarget {
  mesh: Mesh;
  index: number;
}

export interface LoadedAvatar {
  scene: Group;
  mixer: AnimationMixer | null;
  clips: Map<string, AnimationClip>;
  /** Morph targets matching the profile's mouth shapes. May be empty. */
  mouth: MorphTarget[];
  /** Morph targets driven by the blink timer. May be empty. */
  blink: MorphTarget[];
  /** Every material, captured once so the fade-in can restore opacity exactly. */
  materials: Material[];
  /** Height in metres at identity scale, used to normalise. */
  naturalHeight: number;
  /** What the model actually contains — used to report unresolved profile names. */
  available: { clips: Set<string>; morphs: Set<string> };
}

/**
 * Load a model and bind it to a profile.
 *
 * Names are resolved against whatever the model has; anything absent is left out
 * rather than throwing, so a partial or mismatched profile degrades to fewer
 * animations instead of no avatar. `available` carries the full inventory back
 * so the caller can report what did not resolve.
 */
export async function loadAvatar(
  url: string,
  profile: { morphs: { mouth: string[]; blink: string[] } },
): Promise<LoadedAvatar> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);
  const scene = gltf.scene;

  const clips = new Map<string, AnimationClip>();
  for (const clip of gltf.animations) clips.set(clip.name, clip);

  const mixer = gltf.animations.length > 0 ? new AnimationMixer(scene) : null;

  const mouth: MorphTarget[] = [];
  const blink: MorphTarget[] = [];
  const materials: Material[] = [];
  const availableMorphs = new Set<string>();

  scene.traverse((object) => {
    if (!(object instanceof Mesh)) return;

    for (const material of toArray(object.material)) {
      if (!materials.includes(material)) materials.push(material);
    }

    const dictionary = object.morphTargetDictionary;
    if (!dictionary) return;
    for (const name of Object.keys(dictionary)) availableMorphs.add(name);

    // A rig can split the same shape across several meshes (head, teeth, tongue),
    // so collect every match rather than stopping at the first.
    for (const name of profile.morphs.mouth) {
      const index = findMorphIndex(dictionary, name);
      if (index !== undefined) mouth.push({ mesh: object, index });
    }
    for (const name of profile.morphs.blink) {
      const index = findMorphIndex(dictionary, name);
      if (index !== undefined) blink.push({ mesh: object, index });
    }
  });

  return {
    scene,
    mixer,
    clips,
    mouth,
    blink,
    materials,
    naturalHeight: measureHeight(scene),
    available: { clips: new Set(clips.keys()), morphs: availableMorphs },
  };
}

/**
 * Measure with the root forced to identity scale, then restore.
 *
 * `Box3.setFromObject` includes the current scale, and the caller is about to
 * overwrite that scale to hit a target height — so measuring a pre-scaled box
 * would feed the old scale back into the new one.
 */
function measureHeight(scene: Group): number {
  const original = scene.scale.clone();
  scene.scale.set(1, 1, 1);
  scene.updateMatrixWorld(true);

  const size = new Vector3();
  new Box3().setFromObject(scene).getSize(size);

  scene.scale.copy(original);
  scene.updateMatrixWorld(true);

  // Guard against a degenerate model so the caller never divides by zero.
  return size.y > 1e-6 ? size.y : 1;
}

/** Exact match first, then case-insensitive — exporters disagree on casing. */
function findMorphIndex(
  dictionary: Record<string, number>,
  name: string,
): number | undefined {
  if (name in dictionary) return dictionary[name];
  const lowered = name.toLowerCase();
  for (const [key, index] of Object.entries(dictionary)) {
    if (key.toLowerCase() === lowered) return index;
  }
  return undefined;
}

function toArray(material: Material | Material[]): Material[] {
  return Array.isArray(material) ? material : [material];
}
